import { Box, Button, Stack, Typography, Container, Grid, Tabs, Tab } from "@mui/material";
import { ChevronRight, ArrowForward } from "@mui/icons-material";
import { Body1Typography, H5Typography, H4Typography, H6Typography } from "../typography";
import "./styles.scss";
import { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback, useMemo } from "react";
import {
  getPersonalitySurveyContent,
  getAnswerPathForPersonalityType,
  PersonalityType,
  quizColorMap,
  quizColorTextMap,
  quizDescriptionMap,
  personalityCommunicationMap,
  personalityCollaborationMap,
  personalityConnectionMap,
  personalityCommunicationHomeownerMap,
  personalityCollaborationHomeownerMap,
  personalityConnectionHomeownerMap,
  personalityStrengthsMap,
  personalityPerksMap,
  personalityPitfallsMap,
  personalitySuperpowerMap,
  personalitySuperpowerHomeownerMap,
} from "./utils";
import { PeronalityTriangle } from "./triangle";
import womanStrong from "../../assets/designer-profile/woman_strong.png";

const camelCaseToWords = (s: string) => {
  const result = s.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const Circle = ({ color, text }: { color: string; text: string }) => (
  <Stack 
    direction="row" 
    spacing={1} 
    alignItems="center" 
    pl={{ xs: 0, md: 3 }}
    sx={{ minWidth: { xs: "fit-content", md: "auto" } }}
  >
    <Box
      sx={{
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <Body1Typography sx={{ fontSize: { xs: "0.8rem", md: "1rem" } }}>
      {text}
    </Body1Typography>
  </Stack>
);

const defaultPerks = [
  "Fair",
  "Diligent",
  "Focused",
  "Punctual",
  "Structured",
  "Consistent",
  "Serviced Minded",
  "Collaborative Leader",
  "Strong Organizational Skills",
];

const defaultPitfalls = [
  "Bossy",
  "Anxious",
  "Negative",
  "Controlling",
  "Judgmental",
  "Perfectionist",
  "Passive Aggressive Communicator",
  "Micromanager When Trust Is Lacking",
  "Keeps Score Of Right And Wrong Behaviors",
  "Unrealistic Expectations For Self And Others",
];

const getTraitSections = (personalityType: PersonalityType | null) => [
  {
    title: "Strengths",
    items: personalityType ? personalityStrengthsMap[personalityType] : [],
  },
  {
    title: "Perks",
    items: personalityType ? personalityPerksMap[personalityType] : defaultPerks,
  },
  {
    title: "Pitfalls",
    items: personalityType ? personalityPitfallsMap[personalityType] : defaultPitfalls,
  },
];

const getInsightTabs = (
  personalityType: PersonalityType | null,
  maps: {
    communication: Record<PersonalityType, string>;
    collaboration: Record<PersonalityType, string>;
    connection: Record<PersonalityType, string>;
  }
) => [
  {
    label: "Communication",
    title: "Your Communication Style",
    body: personalityType ? maps.communication[personalityType] : "",
  },
  {
    label: "Collaboration",
    title: "Your Collaboration Style",
    body: personalityType ? maps.collaboration[personalityType] : "",
  },
  {
    label: "Connection",
    title: "Your Connection Style",
    body: personalityType ? maps.connection[personalityType] : "",
  },
];

export interface PersonalityQuizRef {
  backStep: () => void;
  reset: () => void;
  advanceWithPrefill: () => boolean;
}

export const PersonalityQuiz = forwardRef<PersonalityQuizRef, { 
  onResultChange?: (result: PersonalityType | null) => void;
  onBeginShown?: () => void;
  onQuizStarted?: () => void;
  onStepChange?: (step: number, totalSteps: number) => void;
  onComplete?: () => void;
  beginLabel?: string;
  skipBegin?: boolean;
  initialResult?: PersonalityType | null;
  autoStartOnInitialResult?: boolean;
  prefillFromInitialResult?: boolean;
  showSuperPowerCard?: boolean;
  onRetake?: () => void;
  contentVariant?: "designer" | "homeowner";
}>(({ onResultChange, onBeginShown, onQuizStarted, onStepChange, onComplete, beginLabel = "Begin", skipBegin = false, initialResult, autoStartOnInitialResult = true, prefillFromInitialResult = false, showSuperPowerCard = false, onRetake, contentVariant = "designer" }, ref) => {
  const [showBegin, setShowBegin] = useState<boolean>(!skipBegin);
  const [endResult, setEndResult] = useState<PersonalityType | null>(null);
  const [answers, setAnswers] = useState<string>("");
  const [question, setQuestion] = useState<string | null>(null);
  const [options, setOptions] = useState<{ id: string; text: string }[]>([]);
  const [insightTab, setInsightTab] = useState(0);
  const lastAnswer = useRef<string>("");
  const hasInteractedRef = useRef<boolean>(false);

  const prefillAnswerPath = useMemo(() => {
    if (!prefillFromInitialResult || !initialResult) return null;
    return getAnswerPathForPersonalityType(initialResult);
  }, [prefillFromInitialResult, initialResult]);

  const prefillNextOptionId = useMemo(() => {
    if (!prefillAnswerPath || !prefillAnswerPath.startsWith(answers)) return null;
    const optionList = Array.isArray(options) ? options : [];
    for (const option of optionList) {
      const candidate = answers + option.id;
      if (prefillAnswerPath.startsWith(candidate)) {
        return option.id;
      }
    }
    return null;
  }, [answers, options, prefillAnswerPath]);

  const advanceWithPrefill = useCallback(() => {
    if (!prefillNextOptionId) return false;
    hasInteractedRef.current = true;
    lastAnswer.current = prefillNextOptionId;
    setAnswers(answers + prefillNextOptionId);
    return true;
  }, [answers, prefillNextOptionId]);

  const applyInitialResult = useCallback(() => {
    if (!initialResult || hasInteractedRef.current) return false;
    const answerPath = getAnswerPathForPersonalityType(initialResult);
    if (!answerPath) return false;

    setAnswers(answerPath);
    setShowBegin(false);
    hasInteractedRef.current = true;
    if (onQuizStarted) onQuizStarted();

    const { result } = getPersonalitySurveyContent(answerPath);
    if (result && onResultChange) {
      onResultChange(result);
    }
    if (result && onComplete) {
      onComplete();
    }
    return true;
  }, [initialResult, onQuizStarted, onResultChange, onComplete]);

  useEffect(() => {
    const {
      question: quizQuestion,
      options: quizOptions,
      result,
    } = getPersonalitySurveyContent(answers);

    setQuestion(quizQuestion);
    setOptions(Array.isArray(quizOptions) ? quizOptions : []);
    setEndResult(result);
    if (onResultChange) onResultChange(result);

    // Calculate current step based on answers length
    if (!showBegin && onStepChange) {
      const currentStep = Math.min(answers.length + 1, 5); // Max 5 steps for personality
      onStepChange(currentStep, 5);
    }

    // Notify completion when result is available
    if (result && onComplete) {
      onComplete();
    }
  }, [answers, showBegin, onResultChange, onStepChange, onComplete]);

  // Handle showBegin state changes
  useEffect(() => {
    if (showBegin && onBeginShown) {
      onBeginShown();
    }
  }, [showBegin, onBeginShown]);

  const handleBack = () => {
    if (answers.length > 0) {
      const newAnswers = answers.slice(0, -1 * lastAnswer.current.length);
      setAnswers(newAnswers);
    }
  };

  // Prefill quiz when initialResult is provided and quiz hasn't been interacted with yet
  useEffect(() => {
    if (!initialResult || hasInteractedRef.current) return;

    if (autoStartOnInitialResult && showBegin) {
      applyInitialResult();
    }
  }, [answers.length, autoStartOnInitialResult, applyInitialResult, initialResult, showBegin]);

  const handleReset = () => {
    setAnswers("");
    setEndResult(null);
    setShowBegin(!skipBegin);
    setQuestion(null);
    setOptions([]);
    lastAnswer.current = "";
    hasInteractedRef.current = false;
    if (onResultChange) onResultChange(null);
  };

  const handleRetakeClick = () => {
    if (onRetake) {
      onRetake();
      return;
    }
    handleReset();
    setShowBegin(false);
    if (onQuizStarted) onQuizStarted();
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Expose back and reset functions to parent component via ref
  useImperativeHandle(ref, () => ({
    backStep: handleBack,
    reset: handleReset,
    advanceWithPrefill,
  }), [advanceWithPrefill, handleBack]);

  const handleOptionClick = (optionId: string) => {
    hasInteractedRef.current = true;
    const newAnswers = answers + optionId;
    lastAnswer.current = optionId;
    setAnswers(newAnswers);
  };

  const colors = endResult ? quizColorMap[endResult] : [];
  const description = endResult ? quizDescriptionMap[endResult] : "";
  const superpowerMap =
    contentVariant === "homeowner"
      ? personalitySuperpowerHomeownerMap
      : personalitySuperpowerMap;
  const superpowerTitle =
    contentVariant === "homeowner" ? "Your Super Power" : "Your Design Super Power";
  const insightTabs = getInsightTabs(endResult, {
    communication:
      contentVariant === "homeowner"
        ? personalityCommunicationHomeownerMap
        : personalityCommunicationMap,
    collaboration:
      contentVariant === "homeowner"
        ? personalityCollaborationHomeownerMap
        : personalityCollaborationMap,
    connection:
      contentVariant === "homeowner"
        ? personalityConnectionHomeownerMap
        : personalityConnectionMap,
  });
  const traitSections = getTraitSections(endResult);
  const optionList = Array.isArray(options) ? options : [];

  // Show Begin screen first
  if (showBegin) {
    return (
      <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
        <Box textAlign="center" py={6}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            When you know yourself, finding the right client or design partner becomes that much easier.<br /> Let's get you started on finding your best matches.
          </Typography>
          <Button 
            variant="outlined" 
            size="large" 
            color="secondary"
            endIcon={<ChevronRight sx={{ width: 16, height: 16 }} />}
            onClick={() => {
              setShowBegin(false);
              if (onQuizStarted) onQuizStarted();
            }}
          >
            {beginLabel}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box mb={3} pt={0}>
      <Box
        sx={{
          mx: "auto",
          maxWidth: "780px",
        }}
      >

        {endResult && (
          <Box mt={2}>
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={8}
              mt={8}
              mb={3}
              justifyContent="center"
            >
              <Box 
                sx={{ 
                  display: { xs: "flex", md: "block" },
                  flexDirection: { xs: "row", md: "column" },
                  alignItems: { xs: "center", md: "center" },
                  justifyContent: { xs: "center", md: "center" },
                  gap: { xs: 2, md: 0 },
                  height: "fit-content",
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden"
                }}
              >
                {/* Column 1: Circles (mobile) / Stacked (desktop) */}
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{
                    alignItems: { xs: "flex-start", md: "flex-start" },
                    justifyContent: { xs: "center", md: "center" }
                  }}
                >
                  <Circle
                    color={colors[0] || "#000"}
                    text={quizColorTextMap[colors[0] as string]}
                  />
                  <Circle
                    color={colors[1] || "#000"}
                    text={quizColorTextMap[colors[1] as string]}
                  />
                  <Circle
                    color={colors[2] || "#000"}
                    text={quizColorTextMap[colors[2] as string]}
                  />
                </Stack>

                {/* Column 2: Triangle */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "fit-content",
                    maxWidth: "100%",
                    overflow: "hidden"
                  }}
                >
                  <PeronalityTriangle
                    color1={colors[0] || "#000"}
                    color2={colors[1] || "#000"}
                    color3={colors[2] || "#000"}
                  />
                </Box>
              </Box>

              <Box>
                <Body1Typography color={"purple" as any}>
                  YOU ARE AN
                </Body1Typography>
                <H5Typography color={"secondary"} mt={1} mb="16px">
                  {camelCaseToWords(endResult)}
                </H5Typography>
                <Body1Typography>{description}</Body1Typography>
              </Box>
            </Stack>
            {showSuperPowerCard && (
              <Box
                sx={{
                  mt: "130px",
                  border: "1px solid #6D63FF",
                  borderRadius: "16px",
                  p: { xs: 2.5, md: 3 },
                  backgroundColor: "#FFFFFF",
                  position: "relative",
                  overflow: "visible",
                  minHeight: { md: 230 },
                }}
              >
                <Box
                  component="img"
                  src={womanStrong}
                  alt="Design super power"
                  sx={{
                    width: { xs: 150, md: 185 },
                    height: "auto",
                    flexShrink: 0,
                    display: "block",
                    position: { xs: "relative", md: "absolute" },
                    left: { md: 35 },
                    top: { md: "auto" },
                    bottom: { md: 0 },
                    transform: { md: "none" },
                    zIndex: 2,
                    mb: { xs: 2, md: 0 },
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                    borderRadius: "14px",
                    pr: { xs: 2.5, md: 4 },
                    pl: { xs: 2.5, md: "calc(185px + 40px)" },
                    py: { xs: 2.5, md: 3 },
                    background: "linear-gradient(90deg, #EAF3FF 0%, #F8EFFF 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#2D3B53",
                      mb: 1,
                      fontSize: { xs: "1rem", md: "1.1rem" },
                    }}
                  >
                    {superpowerTitle}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#55627A",
                      fontSize: { xs: "0.9rem", md: "0.96rem" },
                      lineHeight: 1.6,
                    }}
                  >
                    {endResult ? superpowerMap[endResult] : ""}
                  </Typography>
                </Box>
              </Box>
            )}

            {showSuperPowerCard && (
              <Box sx={{ mt: 6 }}>
                <Grid container spacing={3}>
                  {traitSections.map((section) => (
                    <Grid key={section.title} item xs={12} md={4}>
                      <Box
                        sx={{
                          border: "1px solid #6D63FF",
                          borderRadius: "14px",
                          p: 3,
                          height: "100%",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#C2477A",
                            fontSize: "1rem",
                            mb: 1.5,
                          }}
                        >
                          {section.title}
                        </Typography>
                        <Box
                          sx={{
                            height: "1px",
                            backgroundColor: "#6D63FF",
                            opacity: 0.5,
                            mb: 2,
                          }}
                        />
                        <Stack spacing={1.5}>
                          {(section.items ?? []).map((item) => (
                            <Box
                              key={item}
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.25,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 0,
                                  height: 0,
                                  borderTop: "6px solid transparent",
                                  borderBottom: "6px solid transparent",
                                  borderLeft: "9px solid #0E9F6E",
                                  mt: "4px",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  color: "#2D3B53",
                                  fontSize: "0.92rem",
                                  lineHeight: 1.5,
                                }}
                              >
                                {item}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {showSuperPowerCard && (
              <Box sx={{ mt: 6 }}>
                <Tabs
                  value={insightTab}
                  onChange={(_event, value) => setInsightTab(value)}
                  variant="fullWidth"
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: "#5C3BFF",
                      height: 3,
                      borderRadius: "3px 3px 0 0",
                    },
                  }}
                  sx={{
                    borderBottom: "1px solid #6D63FF",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      color: "#2D3B53",
                      fontSize: "0.95rem",
                    },
                    "& .MuiTab-root.Mui-selected": {
                      color: "#5C3BFF",
                    },
                  }}
                >
                  {insightTabs.map((tab) => (
                    <Tab key={tab.label} label={tab.label} />
                  ))}
                </Tabs>
                <Box sx={{ mt: 4 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#C2477A",
                      fontSize: { xs: "1.4rem", md: "1.7rem" },
                      mb: 2,
                    }}
                  >
                    {insightTabs[insightTab].title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#55627A",
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      lineHeight: 1.7,
                    }}
                  >
                    {insightTabs[insightTab].body}
                  </Typography>
                </Box>
              </Box>
            )}

            {showSuperPowerCard && (
              <Box sx={{ mt: 6 }}>
                <Box
                  sx={{
                    border: "1px solid #B9B9B9",
                    borderRadius: "12px",
                    p: { xs: 2.5, md: 3 },
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#1B1B1B",
                      fontSize: { xs: "1.2rem", md: "1.45rem" },
                      mb: 2,
                    }}
                  >
                    This Color Combo Still Doesn't Feel Right?
                  </Typography>
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForward />}
                    onClick={handleRetakeClick}
                    sx={{
                      color: "#5C3BFF",
                      borderColor: "#5C3BFF",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      px: 2.5,
                    }}
                  >
                    Retake The Quiz
                  </Button>
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        color: "#1B1B1B",
                        mb: 0.5,
                      }}
                    >
                      Disclaimer
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: "#6B6B6B",
                        lineHeight: 1.5,
                      }}
                    >
                      While this information is based on scientific research, individual preferences may differ.
                      This information is for educational purposes only. These suggestions are based on
                      generalizations that may differ from your own experience.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {!endResult && (
          <>
            <H5Typography mt={0} textAlign={"center"} mb="28px">
              {question || "Loading question..."}
            </H5Typography>

            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              mt={2}
              spacing={2}
            >
              {optionList.map((option) => {
                const isPrefilled = option.id === prefillNextOptionId;
                return (
                <Button
                  key={option.id}
                  onClick={() => handleOptionClick(option.id)}
                  color="secondary"
                  variant={isPrefilled ? "contained" : "outlined"}
                  sx={{ 
                    width: "100%", 
                    p: "28px 18px", 
                    fontSize: "14px !important",
                    "&.MuiButton-root": {
                      fontSize: "14px !important"
                    },
                  }}
                >
                  {option.text}
                </Button>
              )})}
            </Stack>
            {optionList.length === 0 && (
              <Typography mt={2} textAlign="center" color="text.secondary">
                Loading options...
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
});

PersonalityQuiz.displayName = 'PersonalityQuiz';
